import { cn } from '@/lib/utils'
import { GraphNode, NodeState } from './GraphNode'
import type { Graph } from './layout'
import { EdgeKind } from './tree'

interface GraphCanvasProps {
  graph: Graph
  /** null while the inspector is closed — the whole diagram reads at full strength. */
  selected: string | null
  onSelect: (service: string) => void
}

/** The whole flow, drawn left to right. Edges under the steps, labels on top. */
export function GraphCanvas({ graph, selected, onSelect }: GraphCanvasProps) {
  const touching = new Set<string>()
  for (const edge of graph.edges) {
    if (edge.from === selected) touching.add(edge.to)
    if (edge.to === selected) touching.add(edge.from)
  }

  const stateFor = (service: string) => {
    if (!selected) return NodeState.RESTING
    if (service === selected) return NodeState.SELECTED
    if (touching.has(service)) return NodeState.ADJACENT
    return NodeState.MUTED
  }

  return (
    <div className="relative" style={{ width: graph.width, height: graph.height }}>
      <svg width={graph.width} height={graph.height} className="absolute inset-0" aria-hidden>
        <defs>
          <marker
            id="ct-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 1 L 7 4 L 0 7 z" className="fill-ink-300" />
          </marker>
          <marker
            id="ct-arrow-live"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 1 L 7 4 L 0 7 z" className="fill-ink" />
          </marker>
        </defs>

        {graph.edges.map((edge) => {
          const live = edge.from === selected || edge.to === selected
          return (
            <path
              key={`${edge.from}-${edge.to}-${edge.label}`}
              d={edge.path}
              fill="none"
              strokeWidth={live ? 1.75 : 1}
              strokeDasharray={edge.isBack ? '4 3' : undefined}
              markerEnd={live ? 'url(#ct-arrow-live)' : 'url(#ct-arrow)'}
              className={cn(
                'transition-all',
                live ? 'stroke-ink' : edge.isJump ? 'stroke-brand-600/50' : 'stroke-ink-300',
                !live && selected && 'opacity-40'
              )}
            />
          )
        })}
      </svg>

      {graph.nodes.map((node) => (
        <GraphNode key={node.service} node={node} state={stateFor(node.service)} onSelect={onSelect} />
      ))}

      {/* Above the steps, so a label wider than the column gap still reads. */}
      {graph.edges
        .filter((edge) => edge.from === selected || edge.to === selected)
        .map((edge) => (
          <span
            key={`label-${edge.from}-${edge.to}-${edge.label}`}
            style={{ left: edge.labelX, top: edge.labelY }}
            className={cn(
              'pointer-events-none absolute z-10 max-w-[190px] -translate-x-1/2 -translate-y-1/2',
              'rounded-full border px-2 py-0.5 text-center text-[10px] leading-tight shadow-sm',
              edge.kind === EdgeKind.ANSWER
                ? 'border-ink-200 bg-surface text-ink'
                : 'border-ink-100 bg-ink-50 text-ink-500 italic'
            )}
          >
            {edge.label}
          </span>
        ))}
    </div>
  )
}
